"use client"

import Image from "next/image"
import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react"

import LocalizedClientLink from "@modules/common/components/localized-client-link"
import type { HomeTrendingBrand } from "@modules/home/types"

import { BLUR_LAYERS } from "./blur-layers"
import type { CarouselController } from "./carousel-controller"
import {
  STAGE_HEIGHT,
  buildRing,
  isVisibleOffset,
  slotGeometry,
  wrapOffset,
} from "./carousel-geometry"
import styles from "./trending-brands.module.css"

/** Il codice di GSAP parte poco prima che la sezione entri in vista. */
const PRELOAD_MARGIN = "600px 0px"

type Props = {
  items: HomeTrendingBrand[]
}

export default function TrendingBrandsCarousel({ items }: Props) {
  const ring = useMemo(() => buildRing(items), [items])
  const size = ring.length
  const interactive = size > 1

  const [active, setActive] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLUListElement>(null)
  const controllerRef = useRef<Promise<CarouselController> | null>(null)

  const ensureController = useCallback(() => {
    if (!controllerRef.current) {
      controllerRef.current = import("./carousel-controller")
        .then(({ createCarouselController }) => {
          const root = rootRef.current
          const stage = stageRef.current
          if (!root || !stage) {
            throw new Error("Carosello non montato")
          }
          return createCarouselController({
            root,
            stage,
            slides: Array.from(
              stage.querySelectorAll<HTMLElement>("[data-slide]")
            ),
            reducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)")
              .matches,
            onActiveChange: setActive,
          })
        })
        .catch((error) => {
          controllerRef.current = null
          throw error
        })
    }
    return controllerRef.current
  }, [])

  useEffect(() => {
    const root = rootRef.current
    if (!interactive || !root) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect()
          ensureController().catch(() => undefined)
        }
      },
      { rootMargin: PRELOAD_MARGIN }
    )
    observer.observe(root)

    return () => {
      observer.disconnect()
      controllerRef.current?.then((controller) => controller.destroy())
      controllerRef.current = null
    }
  }, [interactive, ensureController])

  // Se il focus è su una card e il carosello scorre, il focus la segue.
  useEffect(() => {
    const focused = document.activeElement
    if (!(focused instanceof HTMLElement) || !focused.closest("[data-slide]")) {
      return
    }
    const link = stageRef.current?.querySelector<HTMLElement>(
      `[data-index="${active}"] a`
    )
    if (link && link !== focused) {
      link.focus({ preventScroll: true })
    }
  }, [active])

  const step = (delta: number) => {
    ensureController()
      .then((controller) => controller.step(delta))
      .catch(() => undefined)
  }

  const activeItemIndex = ring[active]?.itemIndex

  return (
    <div
      ref={rootRef}
      className={styles.viewport}
      style={{ "--tb-stage-h": STAGE_HEIGHT } as CSSProperties}
      role="region"
      aria-roledescription="carosello"
      aria-label="Brand di tendenza"
    >
      <ul
        ref={stageRef}
        className={styles.stage}
        data-interactive={interactive || undefined}
      >
        {ring.map((slot, index) => {
          const { item } = slot
          const isActive = index === active
          // Una sola copia per brand è esposta agli screen reader: quella
          // reale, oppure la copia che sta al centro.
          const hidden =
            slot.itemIndex === activeItemIndex ? !isActive : slot.clone
          const offset = wrapOffset(index, 0, size)
          const { x, width, height } = slotGeometry(offset)

          return (
            <li
              key={index}
              className={styles.slide}
              data-slide
              data-index={index}
              data-active={isActive}
              data-visible={isVisibleOffset(offset)}
              style={
                {
                  "--tb-x": x.toFixed(3),
                  "--tb-w": width.toFixed(3),
                  "--tb-h": height.toFixed(3),
                } as CSSProperties
              }
              role="group"
              aria-roledescription="slide"
              aria-label={`${slot.itemIndex + 1} di ${items.length}`}
              aria-hidden={hidden || undefined}
            >
              <LocalizedClientLink
                href={`/shop/${encodeURIComponent(item.brandHandle)}`}
                className={styles.link}
                draggable={false}
                tabIndex={isActive ? 0 : -1}
              >
                <Image
                  src={item.image.url}
                  alt={item.alt || item.label}
                  fill
                  sizes="40vw"
                  draggable={false}
                  className={styles.image}
                />
                {BLUR_LAYERS.map(({ blur, from, to }) => (
                  <span
                    aria-hidden="true"
                    className={styles.blurLayer}
                    key={blur}
                    style={
                      {
                        "--tb-blur": `${blur}px`,
                        "--tb-from": `${from}%`,
                        "--tb-to": `${to}%`,
                      } as CSSProperties
                    }
                  />
                ))}
                <span className={styles.label} aria-hidden="true">
                  {item.label}
                </span>
              </LocalizedClientLink>
            </li>
          )
        })}
      </ul>

      {interactive && (
        <>
          <button
            type="button"
            className={`${styles.arrow} ${styles.arrowPrev}`}
            aria-label="Brand precedente"
            onClick={() => step(-1)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m14.5 5-7 7 7 7" />
            </svg>
          </button>
          <button
            type="button"
            className={`${styles.arrow} ${styles.arrowNext}`}
            aria-label="Brand successivo"
            onClick={() => step(1)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m9.5 5 7 7-7 7" />
            </svg>
          </button>
        </>
      )}
    </div>
  )
}
